import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useUser } from "../hooks/useUser";
import "../styles/LeaderboardPage.css";

const PERIODS = [
  { value: "alltime", label: "All Time" },
  { value: "week",    label: "This Week" },
];

const RANK_MEDALS = { 1: "🥇", 2: "🥈", 3: "🥉" };
const TOTAL_ROWS  = 10;

export default function LeaderboardPage() {
  const { user } = useUser();
  const navigate  = useNavigate();

  const [period,     setPeriod]     = useState("alltime");
  const [entries,    setEntries]    = useState([]);
  const [loading,    setLoading]    = useState(true);
  const [lastRefresh,setLastRefresh]= useState(null);
  const [ready,      setReady]      = useState(false);

  useEffect(() => {
    const id = setTimeout(() => setReady(true), 240);
    return () => clearTimeout(id);
  }, []);

  const fetchLeaderboard = useCallback(async () => {
    setLoading(true);
    try {
      const res  = await fetch(`/api/leaderboard?period=${period}&page=1`);
      const data = await res.json();
      console.log(data.entries);
      setEntries(data.entries || []);
      setLastRefresh(data.refreshed_at ? new Date(data.refreshed_at) : null);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  }, [period]);

  useEffect(() => { fetchLeaderboard(); }, [fetchLeaderboard]);

  // Pad to always show TOTAL_ROWS rows
  const paddedEntries = [
    ...entries,
    ...Array.from({ length: Math.max(0, TOTAL_ROWS - entries.length) }, (_, i) => ({
      _placeholder: true,
      rank: entries.length + i + 1,
    })),
  ];

  // All Time → Level, Played, Streak
  // This Week → Wins, Avg., Streak
  const cols = period === "alltime"
    ? { a: "Level", b: "Played", c: "Streak" }
    : { a: "Wins",  b: "Avg. Attempt",   c: "Streak" };

  if (!ready) return <div className="lb-root" />;

  return (
    <div className="lb-root">
      <div className="lb-wrapper">

        <div className="lb-page-title">
          <h1 className="lb-title">Leaderboard</h1>
        </div>

        <div className="lb-panel">
          <div className="lb-panel-content">

            {/* Filters */}
            <div className="lb-filters">
              <div className="lb-filter-group">
                {PERIODS.map(p => (
                  <button
                    key={p.value}
                    className={`lb-filter-btn ${period === p.value ? "active" : ""}`}
                    onClick={() => setPeriod(p.value)}
                  >{p.label}</button>
                ))}
              </div>
            </div>

            <div className="lb-divider" />

            {/* Table header */}
            <div className="lb-table-head">
              <span className="lb-col-rank">#</span>
              <span className="lb-col-player">Player</span>
              <span className="lb-col-stat">{cols.a}</span>
              <span className="lb-col-wins">{cols.b}</span>
              <span className="lb-col-streak">{cols.c}</span>
            </div>

            {/* Rows */}
            <div className="lb-table-body">
              {loading ? (
                Array.from({ length: TOTAL_ROWS }).map((_, i) => (
                  <div key={i} className="lb-row lb-row--placeholder" style={{ animationDelay: `${i * 30}ms` }}>
                    <span className="lb-col-rank"><span className="lb-rank-num">{i + 1}</span></span>
                    <span className="lb-col-player">
                      <div className="lb-avatar-wrap"><div className="lb-avatar lb-avatar--ph" /></div>
                      <span className="lb-username lb-username--ph" />
                    </span>
                    <span className="lb-col-stat lb-ph-dash">—</span>
                    <span className="lb-col-wins lb-ph-dash">—</span>
                    <span className="lb-col-streak lb-ph-dash">—</span>
                  </div>
                ))
              ) : (
                paddedEntries.map((e, i) => {
                  if (e._placeholder) return (
                    <div key={`ph-${i}`} className="lb-row lb-row--placeholder" style={{ animationDelay: `${i * 40}ms` }}>
                      <span className="lb-col-rank"><span className="lb-rank-num">{e.rank}</span></span>
                      <span className="lb-col-player">
                        <div className="lb-avatar-wrap"><div className="lb-avatar lb-avatar--ph" /></div>
                        <span className="lb-username lb-username--ph" />
                      </span>
                      <span className="lb-col-stat lb-ph-dash">—</span>
                      <span className="lb-col-wins lb-ph-dash">—</span>
                      <span className="lb-col-streak lb-ph-dash">—</span>
                    </div>
                  );

                  const isOwn = user && e.user_id === user.id;
                  return (
                    <div
                      key={e.user_id}
                      className={`lb-row ${isOwn ? "own" : ""}`}
                      onClick={() => navigate(`/profile/${e.username}`)}
                      style={{ animationDelay: `${i * 40}ms` }}
                    >
                      <span className="lb-col-rank">
                        {RANK_MEDALS[e.rank] ?? <span className="lb-rank-num">{e.rank}</span>}
                      </span>
                      <span className="lb-col-player">
                        <div className="lb-avatar-wrap">
                          {e.avatar_url
                            ? <img src={e.avatar_url} alt="" className="lb-avatar" />
                            : <div className="lb-avatar lb-avatar--empty">🧙</div>
                          }
                          {e.border_url && (
                            <img src={e.border_url} alt="" className="lb-border-overlay" />
                          )}
                        </div>
                        <span className="lb-username">{e.username}</span>
                        {e.fraction && (
                          <img src={`/${e.fraction.toLowerCase()}.png`} alt={e.fraction} className="lb-fraction" />
                        )}
                        {isOwn && <span className="lb-you">you</span>}
                      </span>
                      <span className="lb-col-stat lb-val-gold">
                        {period === "alltime" ? (e.level ?? "—") : (e.wins ?? "—")}
                      </span>
                      <span className="lb-col-wins">
                        {period === "alltime" ? (e.played ?? "—") : (e.avg_attempts ?? "—")}
                      </span>
                      <span className="lb-col-streak">{e.daily_streak ?? "—"}</span>
                    </div>
                  );
                })
              )}
            </div>

            <div className="lb-divider" />

            {/* Footer */}
            {lastRefresh && (
              <div className="lb-footer-row">
                <span className="lb-last-refresh">
                  Updated {lastRefresh.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}
                </span>
              </div>
            )}

          </div>
        </div>

      </div>
    </div>
  );
}